import { logger } from 'packages/logger';

import { getSearchResults } from './api';
import pageActions from '../page/actions';
import { API_ERRORS } from 'tradera/utils/api';
import { reload } from 'tradera/utils/location';
import initialState from './initial-state';
import * as discoverTracking from 'tradera/apps/tradera/components/discover/helpers/search-gtm';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import { replacePartInPath } from 'tradera/utils/url';
import {
    handleSearchResultsError,
    requestSearchResults,
    initialize as initReducer,
    receiveSearchResults as receiveSearchResultsReducerAction
} from './reducer';
import * as itemActions from 'tradera/state/items/actions';

const updatePageMetaForSearchResults = () => (dispatch, getState) => {
    const discover = getState().discover;
    const { reduxContainer } = discover;
    const { pageMeta } = reduxContainer;
    dispatch(
        pageActions.setPageMeta({
            ...pageMeta
        })
    );
};

/**
 *
 * @param {object} payload the normalized version of the response
 * @param {string} payload.endpoint
 * @param {object} payload.queryParams
 * @param {object} payload.result
 * @param {object} [?payload.result.categoryPath]
 * @param {object[]} [payload.result.items]
 * @param {object} [payload.result.itemsOnDisplay]
 * @param {object[]} [payload.result.itemsOnDisplay.items]
 * @param {object} [payload.result.reduxContainer]
 * @param {object} [payload.result.reduxContainer.ads]
 * @param {object} [payload.result.reduxContainer.pageMeta]
 */
export const receiveSearchResults =
    (payload, shippingRegionCountryCodeIso2) => (dispatch) => {
        dispatch(
            receiveSearchResultsReducerAction({
                ...payload,
                shippingRegionCountryCodeIso2
            })
        );
        const items = payload.result?.items || [];
        dispatch(
            itemActions.addItemsToWishList(
                items
                    .filter((item) => item.inWishList)
                    .map((item) => item.itemId)
            )
        );
    };

export const fetchSearchResults = (
    endpoint,
    queryParams,
    pageType,
    onLoaded
) => {
    return async (dispatch, getState) => {
        dispatch(requestSearchResults());
        const {
            environment: { version }
        } = getState();

        return getSearchResults(endpoint, queryParams, '', version)
            .then(({ data: result }) => {
                const { isRedirect, fromCategoryId, toCategoryId } = result;

                if (isRedirect) {
                    const newPathname = replacePartInPath(
                        window.location.pathname,
                        `${fromCategoryId}`,
                        `${toCategoryId}`
                    );
                    const newUrl = newPathname + window.location.search;
                    window.location.replace(newUrl);
                    return;
                }

                const searchResults = { result, endpoint, queryParams };
                const shippingRegionCountryCodeIso2 =
                    selectShippingRegionCountryCodeIso2(getState());
                dispatch(
                    receiveSearchResults(
                        searchResults,
                        shippingRegionCountryCodeIso2
                    )
                );
                return searchResults;
            })
            .catch((error) => {
                switch (error.message) {
                    case API_ERRORS.CANCELLED:
                        break;
                    case API_ERRORS.VERSION_MISMATCH:
                        reload(true);
                        break;
                    default:
                        dispatch(handleSearchResultsError(error.message));
                        break;
                }
                switch (error.message) {
                    case API_ERRORS.CANCELLED:
                    case API_ERRORS.NETWORK:
                    case API_ERRORS.TIMEOUT:
                    case API_ERRORS.VERSION_MISMATCH:
                        break;
                    default:
                        logger(error);
                        break;
                }
            })
            .finally(() => {
                dispatch(afterReceiveSearchResults(pageType, onLoaded));
            });
    };
};

export const afterReceiveSearchResults =
    (pageType, onLoaded) => (dispatch, getState) => {
        dispatch(updatePageMetaForSearchResults(pageType));
        dispatch(() => {
            trackNewSearch(getState);
            onLoaded();
        });
    };

export const initialize = () => (dispatch) => {
    dispatch(initReducer(initialState()));
};

const trackNewSearch = (getState) => {
    const state = getState();
    const { categoryPath, items, metaTitle } = state.discover;
    discoverTracking.trackNewSearch(
        categoryPath,
        items.map((x) => x.itemId),
        metaTitle
    );
};
